import { YouTubePlayer } from 'youtube-player/dist/types';
import pubSub from '../utility/pub-sub';
import { VIDEO_FINISH, VIDEO_PLAY, VIDEO_STOP } from '../utility/constants';

/**
   * When video is ready Youtube APi run this callback to have control over video
   * @param video Youtube Video Iframe instance
   * @param autoplay 0-false 1-true
   * @param loop 0-false 1-true
   * @param mute 0-false 1-true
   */
export const onPlayerReady = (
  video: YouTubePlayer,
  autoplay: number,
  loop: number,
  mute: number,
) => {
  if (mute) {
    video.mute();
  }

  video.setLoop(loop === 1);

  if (autoplay === 1) {
    video.playVideo();
  }
};

/**
 * Create number between 100 and 999
 * @returns string eg. 113 or similar number in string format
 */
export const setVideoId = () => {
  const minVal = 100;
  const maxVal = 999;

  return Math.round(minVal + Math.random() * (maxVal - minVal)).toString();
};

/**
 * Get info of video such as current time, video durationand and proggress percent
 * @param YTPlayer
 * @returns time-related information of the video file
 */
const getTimeInfo = async (YTPlayer: YouTubePlayer) => {
  const currentTime = await YTPlayer.getCurrentTime() || 0;
  const duration = await YTPlayer.getDuration();
  const percent = Math.round((currentTime && duration ? (currentTime * 100) / duration : 0));
  return {
    currentTime,
    duration,
    percent,
  };
};

/**
 *
 * @param YTPlayer
 * @param event - GA4 event name
 * @returns GA4 object model for dataLayer
 */
export const getVideoInfo = async (YTPlayer: YouTubePlayer, event: string) => {
  const videoUrl = YTPlayer.getVideoUrl() || '';
  const { currentTime, duration, percent } = await getTimeInfo(YTPlayer);
  const { title } = await YTPlayer.getIframe();

  return {
    event,
    ga4_data: {
      video_provider: 'YouTube',
      video_title: title,
      video_url: videoUrl,
      video_current_time: Math.round(currentTime),
      video_percent: percent,
      video_duration: duration,
      visible: 1,
    },
  };
};

/**
 * Get current video proggress
 * @param youtubeVideo
 * @returns percentage number
 */
export const getPercentage = async (youtubeVideo: YouTubePlayer) => {
  const { percent } = await getTimeInfo(youtubeVideo);
  const trackPercentage = Math.round(percent);

  return trackPercentage;
};

/**
 * Push progress event data to dataLayer
 * @param youtubeVideo
 */
export const intervalTracker = async (youtubeVideo: YouTubePlayer) => {
  const data = await getVideoInfo(youtubeVideo, 'ga4_video_progress');
  window.dataLayer.push(data);
};

/**
   * Callback for Youtube actions such as play, pause, ...
   * @param youtubeVideo
   * @param videoStatus playing, paused, ended
   */
export const onPlayerStateChange = async (
  youtubeVideo: YouTubePlayer,
  videoStatus: number,
  videoPlayers: YouTubePlayer[],
  isVideoPlays: () => boolean,
  setVideoPlays: (isPlaying: boolean) => void,
) => {
  const youtubeIframe = await youtubeVideo.getIframe();
  const currentPlayerId = youtubeIframe.id;

  // Notify listeners that youtube video play
  if (videoStatus === window.YT.PlayerState.PLAYING) {
    videoPlayers.forEach(async (video) => {
      const playerVideoElement = await video.getIframe();
      const iframeId = playerVideoElement.id;
      if (
        iframeId
        && iframeId !== currentPlayerId
        && Object.prototype.hasOwnProperty.call(video, 'stopVideo')
      ) {
        video.stopVideo();
      }
    });

    pubSub.publish(VIDEO_PLAY, {
      currentPlayerId,
    });

    if (!isVideoPlays()) {
      const data = await getVideoInfo(youtubeVideo, 'ga4_video_start');
      window.dataLayer.push(data);
      setVideoPlays(true);
    }
  }

  if (videoStatus === window.YT.PlayerState.PAUSED) {
    pubSub.publish(VIDEO_STOP, {
      currentPlayerId,
    });
  }

  if (videoStatus === window.YT.PlayerState.ENDED) {
    pubSub.publish(VIDEO_FINISH, {
      currentPlayerId,
    });
    const data = await getVideoInfo(youtubeVideo, 'ga4_video_complete');
    window.dataLayer.push(data);
    setVideoPlays(false);

    if (youtubeIframe.dataset.loop === '1') {
      youtubeVideo.playVideo();
    }
  }
};
